/**
 * ONE TAP — the top-level deterministic tick.
 *
 * `stepWorld` is the one function the Durable Object and the client both call to
 * advance the simulation. It is a fixed step (TICK_DT) and it is pure with
 * respect to its inputs: same WorldState + same InputCommands => same WorldState,
 * on the server and in the browser. Everything that could diverge (iteration
 * order, randomness, wall-clock time) is pinned down here.
 *
 * NOTE: this file runs inside a Cloudflare Worker. No `three`, no DOM.
 */

import { DASH_CHARGES_UNARMED, TEAM_A, TEAM_B, TICK_DT, TICK_MS } from '@shared/constants';
import { clone, copy, set, v3 } from '@shared/math';
import { GunState, HealthState, HitZone, MoveState, RoundPhase } from '@shared/types';
import type {
  ArenaData,
  CollisionWorld,
  GameEvent,
  InputCommand,
  PlayerState,
  WorldState,
} from '@shared/types';
import { recordHistory } from '@shared/sim/combat';
import {
  heldGunOf,
  resolveCatches,
  stepGun,
  tryFire,
  tryPickup,
  tryPulse,
  tryThrow,
} from '@shared/sim/gun';
import { stepMovement } from '@shared/sim/movement';
import { createGunEntity, registerWorld, stepMatch } from '@shared/sim/match';

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/**
 * A brand-new world: two guns (one per team, parked chambered on their spawn
 * pads) and a match sitting in Lobby. Nothing is live until the room calls
 * `beginRound`.
 */
export function createWorld(arena: ArenaData, seed: number): WorldState {
  const gunA = createGunEntity(0, TEAM_A);
  const gunB = createGunEntity(1, TEAM_B);
  copy(gunA.pos, arena.gunSpawnA);
  copy(gunB.pos, arena.gunSpawnB);

  const w: WorldState = {
    tick: 0,
    timeMs: 0,
    players: {},
    guns: [gunA, gunB],
    match: {
      phase: RoundPhase.Lobby,
      round: 0,
      scoreA: 0,
      scoreB: 0,
      phaseEndsAtMs: 0,
      roundEndsAtMs: 0,
      suddenDeath: false,
      lastRoundWinner: -1,
      winner: -1,
    },
  };

  registerWorld(w, arena, seed);
  return w;
}

/**
 * A fully-populated PlayerState. Every field is initialized — no `undefined`
 * anywhere — because the delta-snapshot encoder diffs field by field and would
 * otherwise silently drop keys.
 *
 * A fresh player starts **Dead**. Rounds are elimination with no mid-round
 * respawn (RESPAWN_DELAY_MS = 0 / DESIGN.md §2), so somebody who joins while a
 * round is running sits it out and comes in at the next `beginRound`, which
 * revives and spawns everyone. It also keeps a player who has never been placed
 * in the arena from counting toward "team still alive" while standing at the
 * origin.
 */
export function createPlayer(id: string, name: string, team: number, bot: boolean): PlayerState {
  return {
    id,
    name,
    team,
    bot,

    pos: v3(),
    vel: v3(),
    yaw: 0,
    pitch: 0,

    moveState: MoveState.Airborne,
    healthState: HealthState.Dead,
    health: 0,

    grounded: false,
    crouching: false,
    wallNormal: null,
    wallSurfaceId: -1,
    wallRunElapsedMs: 0,

    dashCharges: DASH_CHARGES_UNARMED,
    dashCooldownMs: 0,
    dashElapsedMs: 0,
    dashDir: v3(),

    slideElapsedMs: 0,
    // AIR jumps only — see resetPlayerForRound in match.ts. movement.ts refills
    // this to 1 on ground contact; the ground jump itself is gated by `grounded`.
    jumpsRemaining: 1,
    coyoteMs: 0,
    jumpBufferMs: 0,

    grappleAnchorId: -1,
    grapplePoint: null,
    grappleLength: 0,
    grappleElapsedMs: 0,
    grappleCooldownMs: 0,
    grappleHookProgress: 0,

    heldGun: -1,
    fireCooldownMs: 0,
    throwChargeMs: 0,
    catchLockoutMs: 0,
    pulseCooldownMs: 0,

    kills: 0,
    deaths: 0,
    catches: 0,
    intercepts: 0,

    connected: true,
    latencyMs: 0,
  };
}

// ---------------------------------------------------------------------------
// Input plumbing
// ---------------------------------------------------------------------------

/**
 * Last command seen per player, per world. A dropped packet must not stop a
 * player dead — that reads as a rubber-band and it deletes momentum, which is
 * the cardinal sin (DESIGN.md §3). Instead the last command is replayed with the
 * buttons cleared: you keep steering and keep your speed, you just do not fire,
 * jump or dash on a frame the server never heard about.
 */
const commandCache = new WeakMap<WorldState, Map<string, InputCommand>>();

function cacheFor(w: WorldState): Map<string, InputCommand> {
  let m = commandCache.get(w);
  if (!m) {
    m = new Map<string, InputCommand>();
    commandCache.set(w, m);
  }
  return m;
}

function cloneCommand(c: InputCommand): InputCommand {
  return {
    seq: c.seq,
    dt: c.dt,
    moveX: c.moveX,
    moveZ: c.moveZ,
    yaw: c.yaw,
    pitch: c.pitch,
    buttons: c.buttons,
    clientTimeMs: c.clientTimeMs,
  };
}

/** dt is client-supplied. Never let it exceed the fixed step, or a client can buy speed. */
function safeDt(dt: number): number {
  if (!(dt > 0)) return TICK_DT;
  return dt > TICK_DT ? TICK_DT : dt;
}

/**
 * The command to simulate this tick.
 *
 * - live input, sane dt  -> used as-is (no copy, no allocation)
 * - live input, bad dt   -> copy with dt clamped to the fixed step
 * - no input this tick   -> the cached command with buttons cleared (coast)
 * - frozen phase         -> look around, but no movement intent and no actions
 */
function effectiveCommand(
  w: WorldState,
  p: PlayerState,
  incoming: InputCommand | undefined,
  live: boolean,
): InputCommand {
  const cache = cacheFor(w);

  let base: InputCommand;
  if (incoming) {
    base = incoming;
    cache.set(p.id, cloneCommand(incoming));
  } else {
    let coast = cache.get(p.id);
    if (!coast) {
      coast = {
        seq: 0,
        dt: TICK_DT,
        moveX: 0,
        moveZ: 0,
        yaw: p.yaw,
        pitch: p.pitch,
        buttons: 0,
        clientTimeMs: 0,
      };
      cache.set(p.id, coast);
    }
    coast.dt = TICK_DT;
    coast.buttons = 0; // held buttons do not survive a gap in the stream
    base = coast;
  }

  if (!live) {
    // Warmup / RoundEnd / MatchEnd: aim freely, act not at all.
    return {
      seq: base.seq,
      dt: safeDt(base.dt),
      moveX: 0,
      moveZ: 0,
      yaw: base.yaw,
      pitch: base.pitch,
      buttons: 0,
      clientTimeMs: base.clientTimeMs,
    };
  }

  if (base.dt === safeDt(base.dt)) return base;
  const fixed = cloneCommand(base);
  fixed.dt = safeDt(base.dt);
  return fixed;
}

/** Deterministic player iteration. Object key order is insertion order and differs per peer. */
function sortedIds(w: WorldState): string[] {
  const ids: string[] = [];
  for (const id in w.players) ids.push(id);
  ids.sort();
  return ids;
}

// ---------------------------------------------------------------------------
// The void
// ---------------------------------------------------------------------------

/**
 * Falling out of the arena kills you. Guarded on `healthState` so that if the
 * movement layer already handled the kill plane this is a no-op rather than a
 * duplicate death.
 *
 * Any gun in hand is released where the player was, still holding whatever was
 * in the chamber; the gun's own physics then carries it into the void and
 * `stepGun` respawns it. Denying the enemy gun by riding it into the pit is a
 * legitimate play (DESIGN.md §1), so the gun is not teleported to safety here.
 */
function enforceKillPlane(
  p: PlayerState,
  arena: ArenaData,
  w: WorldState,
  events: GameEvent[],
): void {
  if (p.healthState === HealthState.Dead) return;
  if (p.pos.y >= arena.killPlaneY) return;

  const gun = heldGunOf(p, w);
  if (gun) {
    gun.holder = null;
    gun.lastThrower = p.id;
    gun.state = GunState.InFlight;
    copy(gun.pos, p.pos);
    copy(gun.vel, p.vel);
    gun.airborneMs = 0;
    gun.throwOrigin = clone(p.pos);
    gun.idleMs = 0;
  }
  p.heldGun = -1;

  p.healthState = HealthState.Dead;
  p.health = 0;
  p.deaths += 1;
  p.moveState = MoveState.Airborne;
  p.grounded = false;
  p.crouching = false;
  p.wallNormal = null;
  p.wallRunElapsedMs = 0;
  p.grappleAnchorId = -1;
  p.grapplePoint = null;
  p.grappleHookProgress = 0;
  set(p.vel, 0, 0, 0);

  // Self-inflicted: it counts as a death but credits nobody a kill.
  events.push({
    t: 'kill',
    killer: p.id,
    victim: p.id,
    zone: HitZone.None,
    gunId: gun ? gun.id : -1,
  });
}

// ---------------------------------------------------------------------------
// The tick
// ---------------------------------------------------------------------------

/**
 * Advance the world exactly one fixed step.
 *
 * Order is load-bearing and must not be shuffled:
 *   1. snapshot player positions for lag-compensated hit rewind
 *   2. movement for every connected, living player
 *   3. their actions, resolved against the positions movement just produced
 *   4. gun physics
 *   5. catches (after guns have moved, so an intercept this tick is possible)
 *   6. match/phase logic, which reads the results of everything above
 *   7. advance the clock
 */
export function stepWorld(
  w: WorldState,
  world: CollisionWorld,
  inputs: Map<string, InputCommand>,
  events: GameEvent[],
): void {
  // 1. Position history for lag compensation.
  recordHistory(w);

  const phase = w.match.phase;
  const live = phase === RoundPhase.Live;
  const inPlay = phase !== RoundPhase.Lobby;
  const arena = world.arena;
  const ids = sortedIds(w);

  // 2 + 3. Movement, then actions, per player.
  for (let i = 0; i < ids.length; i++) {
    const p = w.players[ids[i]];
    if (!p.connected) continue;

    // Cache the command even for dead players so a respawn does not inherit a
    // stale one, but never simulate them.
    const cmd = effectiveCommand(w, p, inputs.get(p.id), live);
    if (p.healthState === HealthState.Dead) continue;
    if (!inPlay) continue;

    stepMovement(p, cmd, world, events);

    if (live) {
      tryFire(p, cmd, w, world, events);
      tryThrow(p, cmd, w, events);
      tryPulse(p, cmd, w, events);
      tryPickup(p, w, events);
    }

    enforceKillPlane(p, arena, w, events);
  }

  if (inPlay) {
    // 4. Gun physics.
    for (let i = 0; i < w.guns.length; i++) stepGun(w.guns[i], w, world, events);

    // 5. Catches — resolved once, globally, so two players reaching for the same
    //    pass are decided by one rule instead of by iteration order.
    resolveCatches(w, events);
  }

  // 6. Round + match flow.
  stepMatch(w, world, events);

  // 7. Clock.
  w.tick += 1;
  w.timeMs += TICK_MS;
}
